
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type ChatMessage = {
  id: string;
  requisicao_id: string;
  criado_por: string;
  autor_tipo: 'analista' | 'cliente';
  mensagem: string;
  arquivo_url?: string | null;
  tipo_arquivo?: string | null;
  criado_em: string;
};

export const useRequisicaoChat = (requisicaoId: string | undefined) => {
  const queryClient = useQueryClient();

  // Buscar mensagens do chat
  const { data: chatMessages = [], isLoading, error } = useQuery({
    queryKey: ['requisicaoChat', requisicaoId],
    queryFn: async () => {
      if (!requisicaoId) return [];
      const { data, error } = await supabase
        .from('requisicao_chat_mensagens')
        .select('*')
        .eq('requisicao_id', requisicaoId)
        .order('criado_em', { ascending: true });

      if (error) throw error;
      
      // Processar mensagens para criar URLs assinadas para arquivos  
      const messagesWithSignedUrls = await Promise.all(
        (data || []).map(async (message) => {
          if (message.arquivo_url && message.tipo_arquivo?.startsWith('image/')) {
            try {
              let fileName = message.arquivo_url;
              
              // Se for uma URL completa antiga, extrair apenas o nome do arquivo
              if (fileName.includes('/storage/v1/object/')) {
                const parts = fileName.split('/');
                const bucketIndex = parts.findIndex(part => part === 'chat-files');
                if (bucketIndex !== -1 && bucketIndex < parts.length - 1) {
                  // Pegar todos os parts após 'chat-files' e juntar com '/'
                  fileName = parts.slice(bucketIndex + 1).join('/');
                }
              }
              
               const { data: signedData, error: signedError } = await supabase.storage
                 .from('chat-files')  
                 .createSignedUrl(fileName, 3600);

               if (!signedError && signedData) {
                 return { ...message, arquivo_url: signedData.signedUrl };
               } else {
                 console.error('Erro ao criar URL assinada:', signedError);
               }
            } catch (err) {
              console.error('Erro ao criar URL assinada para:', message.arquivo_url, err);
            }
          }
          return message;
        })
      );
      
      return messagesWithSignedUrls as ChatMessage[];
    },
    enabled: !!requisicaoId,
  });

  // Enviar nova mensagem
  const sendMessage = useMutation({
    mutationFn: async (payload: Omit<ChatMessage, 'id' | 'criado_em'>) => {
      const { data, error } = await supabase
        .from('requisicao_chat_mensagens')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;
      return data as ChatMessage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisicaoChat', requisicaoId] });
    },
  });

  // Enviar mensagem com arquivo
  const sendMessageWithFile = useMutation({
    mutationFn: async (payload: { 
      requisicao_id: string;
      criado_por: string;
      autor_tipo: 'analista' | 'cliente';
      mensagem: string;
      arquivo_url?: string;
      tipo_arquivo?: string;
    }) => {
      const { data, error } = await supabase
        .from('requisicao_chat_mensagens')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;
      return data as ChatMessage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisicaoChat', requisicaoId] });
    },
  });

  return { chatMessages, isLoading, error, sendMessage, sendMessageWithFile };
};
